<i18n src="./resources/locales.json"></i18n>

<template>
  <div>
    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      eager
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="close()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              v-if="preOrder.id"
              dark
              text
              @click="print()"
            >
              <v-icon left small>mdi-printer</v-icon>
              {{ $t('print') }}
            </v-btn>
            <v-btn
              dark
              text
              :disabled="isNotValidPreOrder() || isSaving"
              :loading="isSaving"
              @click="save()"
            >
              {{ $t('save') }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-expansion-panels v-model="selectedPanel" accordion focusable>
          <ClientSection
            ref="clientSection"
            :orderId="preOrder.id || '-1'"
            :note="preOrder.note"
            :sectionName="$t('client')"
            @validated-client-data="validateClientData"
            @update:note="updateNote"
          />
          <ServicesSection
            ref="servicesSection"
            :sectionName="$t('services')"
            @validated-service-data="validateServiceData"
          />
          <SparePartsSection
            ref="sparePartsSection"
            :sectionName="$t('spareParts')"
            @validated-spare-part-data="validateSparePartData"
          />
        </v-expansion-panels>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showExpiredWarning" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6 warning--text">
          <v-icon left color="warning">mdi-alert-outline</v-icon>
          {{ $t('expiredWarningTitle') }}
        </v-card-title>
        <v-card-text>{{ $t('expiredWarningMessage') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showExpiredWarning = false">
            {{ $t('review') }}
          </v-btn>
          <v-btn color="primary" depressed @click="showExpiredWarning = false">
            {{ $t('accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import {
  BUSINESS_ID_RECTIFICATION,
  DEFAULT_CURRENCY,
  EMPTY_GUID
} from '@/constants/ConfigurationConstants';
import { mapActions, mapGetters } from 'vuex';
import ClientSection from '@/components/order/rectifier-order-detail-modal/sections/client-section/ClientSection.vue';
import ServicesSection from '@/components/order/rectifier-order-detail-modal/sections/services-section/ServicesSection.vue';
import SparePartsSection from '@/components/order/rectifier-order-detail-modal/sections/spare-parts-section/SparePartsSection.vue';

const PRE_ORDER_VALIDITY_DAYS = 15;

const defaultPreOrder = () => ({
  id: null,
  businessId: BUSINESS_ID_RECTIFICATION,
  client: {
    id: EMPTY_GUID,
    name: '',
    phone: null,
    address: '',
    ci: null
  },
  detail: {
    item: {
      specification: { engine: '', brand: '', numberOfCylinders: '' },
      spareParts: [],
      sparePartsDiscount: 0
    },
    services: []
  },
  currencyCode: DEFAULT_CURRENCY,
  servicesDiscount: 0,
  note: '',
  creationDate: null
});

export default {
  name: 'RectifierPreOrderDetailModal',

  components: {
    ClientSection,
    ServicesSection,
    SparePartsSection
  },

  data: () => ({
    dialog: false,
    showExpiredWarning: false,
    isClientDataValid: false,
    isServiceDataValid: false,
    isSparePartDataValid: false,
    selectedPanel: 0,
    isSaving: false,
    preOrder: defaultPreOrder()
  }),

  computed: {
    ...mapGetters('userStorage', ['getUserStorageFullName']),

    formTitle() {
      return this.preOrder.id ? this.$t('editPreOrder') : this.$t('newPreOrder');
    }
  },

  methods: {
    ...mapActions('preOrders', ['addPreOrder', 'updatePreOrder']),

    open(preOrderToEdit) {
      this.preOrder = preOrderToEdit
        ? JSON.parse(JSON.stringify(preOrderToEdit))
        : defaultPreOrder();

      this.isClientDataValid = false;
      this.isServiceDataValid = false;
      this.isSparePartDataValid = false;
      this.selectedPanel = 0;
      this.isSaving = false;
      this.dialog = true;

      if (preOrderToEdit && preOrderToEdit.id) {
        this.$nextTick(() => {
          this.setClientSectionData();
          this.setServicesSectionData();
          this.setSparePartsSectionData();

          if (this.isExpired(preOrderToEdit)) {
            this.showExpiredWarning = true;
          }
        });
      }
    },

    close() {
      this.$refs.clientSection.cleanSection();
      this.$refs.servicesSection.cleanSection();
      this.$refs.sparePartsSection.cleanSection();
      this.preOrder = defaultPreOrder();
      this.selectedPanel = 0;
      this.isSaving = false;
      this.showExpiredWarning = false;
      this.dialog = false;
    },

    async save() {
      if (this.isNotValidPreOrder()) return;

      this.isSaving = true;
      try {
        const clientData = this.getClientData();
        const engine = this.getEngineData();
        const services = this.getServicesData();
        const spareParts = this.getSparePartsData();

        const payload = {
          businessId: BUSINESS_ID_RECTIFICATION,
          client: clientData,
          detail: JSON.stringify({
            item: {
              specification: engine,
              spareParts: spareParts,
              sparePartsDiscount: this.preOrder.detail.item.sparePartsDiscount || 0
            },
            services: services
          }),
          currencyCode: this.preOrder.currencyCode || DEFAULT_CURRENCY,
          servicesDiscount: this.preOrder.servicesDiscount || 0,
          createdBy: this.getUserStorageFullName || '',
          note: this.preOrder.note || ''
        };

        if (this.preOrder.id) {
          await this.updatePreOrder({ id: this.preOrder.id, preOrderData: payload });
          this.$root.$emit('open-status-message-snackbar', {
            message: this.$t('successUpdated'),
            messageType: 'success'
          });
        } else {
          await this.addPreOrder(payload);
          this.$root.$emit('open-status-message-snackbar', {
            message: this.$t('successCreated'),
            messageType: 'success'
          });
        }

        this.close();
        this.$emit('saved');
      } catch (error) {
        console.error('Error saving pre-order:', error);
        this.$root.$emit('open-status-message-snackbar', {
          message: this.$t('errorSaving'),
          messageType: 'error'
        });
      } finally {
        this.isSaving = false;
      }
    },

    getClientData() {
      const { client } = this.$refs.clientSection.sendClientAndEngineData();
      return Object.assign({}, client);
    },

    getEngineData() {
      const { specification } = this.$refs.clientSection.sendClientAndEngineData();
      return Object.assign({}, specification);
    },

    getServicesData() {
      const raw = this.$refs.servicesSection.sendServicesData();
      const checked = raw.filter(s => s.isChecked === true);
      checked.forEach(s => {
        if (s.subServices) {
          s.subServices = s.subServices.filter(
            ss => ss.isChecked || (ss.id && ss.id.includes('other') && ss.name !== '')
          );
          s.subServices.forEach(ss => {
            delete ss.currencyCode;
            delete ss.fixedPrice;
            delete ss.fixedQuantity;
            delete ss.serviceId;
            delete ss.isChecked;
          });
        }
        delete s.isChecked;
      });
      return checked;
    },

    getSparePartsData() {
      const raw = this.$refs.sparePartsSection.sendSparePartsData();
      return raw
        .filter(sp => sp.isChecked === true)
        .map(sp => ({
          name: sp.name,
          measure: sp.selectedMeasure,
          unitOfMeasure: sp.selectedUnitOfMeasure,
          received: sp.received,
          new: sp.new,
          quantity: sp.quantity,
          parts: sp.parts,
          price: sp.price === '' ? '' : parseInt(sp.price),
          receivedDate: sp.receivedDate,
          brandName: sp.brandName,
          receivedBy: sp.receivedBy,
          receivedById: sp.receivedById
        }));
    },

    setClientSectionData() {
      const { client, detail } = this.preOrder;
      const { item } = detail;
      const { specification } = item;
      this.$refs.clientSection.setClientDataToEdit(Object.assign({}, client));
      this.$refs.clientSection.setSpecificationDataToEdit(Object.assign({}, specification));
      this.$refs.clientSection.setIsQuickServiceDataToEdit(false);
      this.$refs.clientSection.setHasPriorityDataToEdit(false);
    },

    setServicesSectionData() {
      const { detail } = this.preOrder;
      const { services } = detail;
      this.$refs.servicesSection.setServicesDataToEdit(services, [], null, null);
    },

    setSparePartsSectionData() {
      const { detail } = this.preOrder;
      const { item } = detail;
      const { spareParts } = item;
      this.$refs.sparePartsSection.setSparePartsDataToEdit(spareParts);
    },

    isNotValidPreOrder() {
      return !this.isClientDataValid;
    },

    isExpired(preOrder) {
      if (!preOrder || !preOrder.creationDate) return false;
      const expiresAt = moment(preOrder.creationDate).add(PRE_ORDER_VALIDITY_DAYS, 'days');
      return moment().isAfter(expiresAt);
    },

    validateClientData(valid) {
      this.isClientDataValid = valid;
    },

    validateServiceData(valid) {
      this.isServiceDataValid = valid;
    },

    validateSparePartData(valid) {
      this.isSparePartDataValid = valid;
    },

    print() {
      if (this.preOrder.id) {
        this.$root.$emit('print-pre-order-pdf', this.preOrder.id);
      }
    },

    updateNote(newNote) {
      this.preOrder.note = newNote;
    }
  }
};
</script>
